import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import '@/App.css';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';

import ClientLayout from './components/layouts/ClientLayout';
import ClientDashboard from './pages/client/ClientDashboard';
import MyPets from './pages/client/MyPets';
import PetForm from './pages/client/PetForm';
import BookAppointment from './pages/client/BookAppointment';
import Appointments from './pages/client/Appointments';
import ClientInvoices from './pages/client/ClientInvoices';
import ClientNotifications from './pages/client/ClientNotifications';

import AdminLayout from './components/layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBookings from './pages/admin/AdminBookings';
import AdminServices from './pages/admin/AdminServices';
import AdminClients from './pages/admin/AdminClients';
import AdminPets from './pages/admin/AdminPets';
import AdminCalendar from './pages/admin/AdminCalendar';
import AdminStaff from './pages/admin/AdminStaff';
import AdminReports from './pages/admin/AdminReports';
import AdminSettings from './pages/admin/AdminSettings';
import AdminPricing from './pages/admin/AdminPricing';
import AdminMessaging from './pages/admin/AdminMessaging';
import AdminBlockedSlots from './pages/admin/AdminBlockedSlots';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminUpcomingArrivals from './pages/admin/AdminUpcomingArrivals';

import StaffLayout from './components/layouts/StaffLayout';
import StaffDashboard from './pages/staff/StaffDashboard';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const redirect = user.role === 'admin' ? '/admin' : user.role === 'staff' ? '/staff' : '/client';
    return <Navigate to={redirect} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Client-side navigation keeps the previous scroll position, which can leave a
  // newly-rendered page's header scrolled up behind the sticky top nav. Reset on
  // every route change so each page always opens at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // CRITICAL: Detect the Google OAuth callback synchronously before ProtectedRoute runs
  if (location.hash?.includes('token=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />

      {/* Client Routes */}
      <Route path="/client" element={<ProtectedRoute allowedRoles={['client']}><ClientLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/client/dashboard" replace />} />
        <Route path="dashboard" element={<ClientDashboard />} />
        <Route path="pets" element={<MyPets />} />
        <Route path="pets/new" element={<PetForm />} />
        <Route path="pets/:petId" element={<PetForm />} />
        <Route path="book" element={<BookAppointment />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="invoices" element={<ClientInvoices />} />
        <Route path="notifications" element={<ClientNotifications />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="upcoming" element={<AdminUpcomingArrivals />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="pets" element={<AdminPets />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="staff" element={<AdminStaff />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="messaging" element={<AdminMessaging />} />
        <Route path="blocked-slots" element={<AdminBlockedSlots />} />
        <Route path="invoices" element={<AdminInvoices />} />
      </Route>

      {/* Staff Routes */}
      <Route path="/staff" element={<ProtectedRoute allowedRoles={['staff', 'admin']}><StaffLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/staff/dashboard" replace />} />
        <Route path="dashboard" element={<StaffDashboard />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
